import * as Yup from 'yup';
import {
  Alert,
  Button,
  ConfirmDialog,
  FormControlLabel,
  FormRadiobuttonGroup,
  FormTextField,
  Radio,
} from 'ui';
import { CSSProperties, useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import {
  JceVenueCollectionEntryEntity,
  useSetJceVenueCollectionEntriesForCollectionMutation,
} from 'generated/graphql';
import { getCollectionsForGeographyRefetch } from './queries';
import { gql } from '@apollo/client';

gql`
  mutation SetJceVenueCollectionEntriesForCollection(
    $collectionId: Int!
    $entries: [JceVenueCollectionEntryInput!]!
  ) {
    setJceVenueCollectionEntriesForCollection(collectionId: $collectionId, entries: $entries) {
      id
      resyVenueId
      tockVenueId
    }
  }
`;

export interface VenueCollectionDetailFormProps {
  geographyId: string;
  collectionId: string | undefined;
  collectionEntrys: readonly JceVenueCollectionEntryEntity[];
}

type VenueEntryInput = {
  resyVenueId?: number;
  tockVenueId?: number;
};

const getVenueEntryLabel = (
  entry: Pick<VenueEntryInput, 'resyVenueId' | 'tockVenueId'>,
  fallback: string
) => {
  if (entry.resyVenueId != null) {
    return `Resy: ${entry.resyVenueId}`;
  }
  if (entry.tockVenueId != null) {
    return `Tock: ${entry.tockVenueId}`;
  }
  return fallback;
};

type VenueIdFormValues = {
  venueType: 'resy' | 'tock';
  venueId: string;
};

const headingStyle: CSSProperties = {
  margin: 0,
  fontSize: '20px',
  fontWeight: 700,
};

const listContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: 12,
  marginBottom: 20,
};

const chipStyle: CSSProperties = {
  padding: '8px 12px',
  backgroundColor: '#e3f2fd',
  borderRadius: '4px',
  fontSize: '14px',
  fontWeight: 500,
  border: '1px solid #90caf9',
  cursor: 'pointer',
};

const venueIdSchema = Yup.object({
  venueType: Yup.string().oneOf(['resy', 'tock']).required('Venue type is required'),
  venueId: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError('Venue ID must be a number'),
});

const VenueCollectionDetailForm = ({
  geographyId,
  collectionId,
  collectionEntrys,
}: VenueCollectionDetailFormProps) => {
  const [setVenueCollectionEntries] = useSetJceVenueCollectionEntriesForCollectionMutation();
  const [displayedEntries, setDisplayedEntries] = useState<VenueEntryInput[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingRemoveEntryIndex, setPendingRemoveEntryIndex] = useState<number | null>(null);

  useEffect(() => {
    setDisplayedEntries(
      collectionEntrys.map((entry) => ({
        resyVenueId: entry.resyVenueId ?? undefined,
        tockVenueId: entry.tockVenueId ?? undefined,
      }))
    );
  }, [collectionEntrys]);

  const applyEntriesUpdate = async (nextEntries: VenueEntryInput[]) => {
    setErrorMessage(null);

    if (!collectionId) {
      setErrorMessage('Unable to update venue IDs: missing collection ID.');
      return false;
    }

    try {
      const response = await setVenueCollectionEntries({
        variables: {
          collectionId: Number(collectionId),
          entries: nextEntries,
        },
        refetchQueries: getCollectionsForGeographyRefetch(Number(geographyId)),
        awaitRefetchQueries: true,
      });

      const serverEntries = response.data?.setJceVenueCollectionEntriesForCollection;
      if (!serverEntries) {
        throw new Error('No venue entries returned from server');
      }

      setDisplayedEntries(
        serverEntries.map((entry) => ({
          resyVenueId: entry.resyVenueId ?? undefined,
          tockVenueId: entry.tockVenueId ?? undefined,
        }))
      );
      setErrorMessage(null);
      return true;
    } catch {
      setErrorMessage('Failed to update venue IDs. Please try again.');
      return false;
    }
  };

  const handleRemoveVenueEntry = async (entryIndex: number) => {
    const entry = displayedEntries[entryIndex];
    if (!entry) {
      return;
    }

    setPendingRemoveEntryIndex(entryIndex);
  };

  const handleConfirmRemoveVenueEntry = async () => {
    if (pendingRemoveEntryIndex === null) {
      return;
    }

    const nextEntries = displayedEntries.filter((_, index) => index !== pendingRemoveEntryIndex);
    await applyEntriesUpdate(nextEntries);
    setPendingRemoveEntryIndex(null);
  };

  const onSubmit = async (
    values: VenueIdFormValues,
    { resetForm }: FormikHelpers<VenueIdFormValues>
  ) => {
    const parsedVenueId = values.venueId === '' ? undefined : Number(values.venueId);
    const nextEntries = [
      ...displayedEntries,
      values.venueType === 'tock' ? { tockVenueId: parsedVenueId } : { resyVenueId: parsedVenueId },
    ].filter((entry) => entry.resyVenueId !== undefined || entry.tockVenueId !== undefined);

    const didUpdate = await applyEntriesUpdate(nextEntries);
    if (didUpdate) {
      resetForm();
    }
  };

  return (
    <section style={{ marginTop: 24 }} aria-label="Assigned Venue IDs">
      <h3 style={headingStyle}>Assigned Venue IDs</h3>
      {errorMessage && (
        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <Alert variant="negative" title={errorMessage} />
        </div>
      )}
      {displayedEntries.length > 0 ? (
        <div style={listContainerStyle}>
          {displayedEntries.map((venue, index) => (
            <div
              key={`${venue.resyVenueId ?? 'none'}-${venue.tockVenueId ?? 'none'}`}
              style={chipStyle}
              onClick={() => void handleRemoveVenueEntry(index)}
              title="Click to remove"
            >
              {getVenueEntryLabel(venue, '-')}
            </div>
          ))}
        </div>
      ) : (
        <div style={listContainerStyle}>None</div>
      )}
      <Formik<VenueIdFormValues>
        initialValues={{ venueType: 'resy', venueId: '' }}
        validationSchema={venueIdSchema}
        onSubmit={onSubmit}
      >
        {({ values, dirty, isValid, isSubmitting }) => (
          <Form>
            <FormRadiobuttonGroup name="venueType" label="Venue ID Type" row>
              <FormControlLabel value="resy" control={<Radio />} label="Resy" />
              <FormControlLabel value="tock" control={<Radio />} label="Tock" />
            </FormRadiobuttonGroup>
            <FormTextField
              label={values.venueType === 'tock' ? 'Tock Venue ID' : 'Resy Venue ID'}
              name="venueId"
            />
            <Button
              color="primary"
              disabled={!dirty || !isValid || isSubmitting}
              size="large"
              type="submit"
              variant="contained"
              style={{ marginTop: 16 }}
            >
              Add Venue ID
            </Button>
          </Form>
        )}
      </Formik>
      <ConfirmDialog
        open={pendingRemoveEntryIndex !== null}
        isDestructive
        title="Remove venue ID?"
        continueText="Remove"
        onCancel={() => setPendingRemoveEntryIndex(null)}
        onContinue={() => {
          void handleConfirmRemoveVenueEntry();
        }}
      >
        {`Remove ${getVenueEntryLabel(
          pendingRemoveEntryIndex !== null ? (displayedEntries[pendingRemoveEntryIndex] ?? {}) : {},
          'this venue ID'
        )}?`}
      </ConfirmDialog>
    </section>
  );
};

export default VenueCollectionDetailForm;
